import type { CalendarEvent } from '../types';
import './CalendarMonthView.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getEventsForDay(events: CalendarEvent[], year: number, month: number, day: number): CalendarEvent[] {
  const d = new Date(year, month - 1, day);
  const dayStart = new Date(d);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(d);
  dayEnd.setHours(23, 59, 59, 999);
  return events.filter((e) => {
    const start = new Date(e.start_date);
    const end = new Date(e.end_date);
    return start <= dayEnd && end >= dayStart;
  });
}

function getDaysInMonth(year: number, month: number): (number | null)[] {
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  const startPad = first.getDay();
  const daysInMonth = last.getDate();
  const result: (number | null)[] = [];
  for (let i = 0; i < startPad; i++) result.push(null);
  for (let d = 1; d <= daysInMonth; d++) result.push(d);
  const total = result.length;
  const remainder = total % 7;
  if (remainder) for (let i = 0; i < 7 - remainder; i++) result.push(null);
  return result;
}

const categorySlug = (e: CalendarEvent): string =>
  (e.category as { slug?: string })?.slug ?? '';

interface CalendarMonthViewProps {
  events: CalendarEvent[];
  year: number;
  month: number;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onEventClick?: (event: CalendarEvent) => void;
  onDayClick?: (date: Date) => void;
}

export function CalendarMonthView({
  events,
  year,
  month,
  onPrevMonth,
  onNextMonth,
  onEventClick,
  onDayClick,
}: CalendarMonthViewProps) {
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString('en-AU', {
    month: 'long',
    year: 'numeric',
  });
  const days = getDaysInMonth(year, month);
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;

  return (
    <div className="calendar-month-view">
      <div className="calendar-month-nav">
        <button
          type="button"
          className="calendar-month-nav-btn"
          onClick={onPrevMonth}
          aria-label="Previous month"
        >
          ←
        </button>
        <h2 className="calendar-month-title">{monthLabel}</h2>
        <button
          type="button"
          className="calendar-month-nav-btn"
          onClick={onNextMonth}
          aria-label="Next month"
        >
          →
        </button>
      </div>

      <div className="calendar-month-grid" role="grid" aria-label={`Calendar for ${monthLabel}`}>
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="calendar-month-weekday" role="columnheader">
            {wd}
          </div>
        ))}
        {days.map((d, i) => {
          if (d === null) {
            return <div key={`empty-${i}`} className="calendar-month-day calendar-month-day--empty" />;
          }
          const dayEvents = getEventsForDay(events, year, month, d);
          const isToday = isCurrentMonth && today.getDate() === d;
          const dayDate = new Date(year, month - 1, d);
          return (
            <div
              key={d}
              className={`calendar-month-day ${isToday ? 'calendar-month-day--today' : ''} ${
                dayEvents.length > 0 ? 'calendar-month-day--busy' : ''
              }`}
              role="gridcell"
              onClick={onDayClick ? () => onDayClick(dayDate) : undefined}
              onKeyDown={
                onDayClick
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onDayClick(dayDate);
                      }
                    }
                  : undefined
              }
              tabIndex={onDayClick ? 0 : undefined}
              aria-label={onDayClick ? `Open day view for ${dayDate.toLocaleDateString('en-AU')}` : undefined}
            >
              <span className="calendar-month-day-num">{d}</span>
              <div className="calendar-month-day-events">
                {dayEvents.slice(0, 4).map((event) => {
                  const slug = categorySlug(event);
                  const content = (
                    <>
                      <span className={`calendar-month-event-dot calendar-month-event-dot--${slug}`} />
                      <span className="calendar-month-event-title">{event.title}</span>
                    </>
                  );
                  return (
                    <div key={event.id} className={`calendar-month-event calendar-month-event--${slug}`}>
                      {onEventClick ? (
                        <button
                          type="button"
                          className="calendar-month-event-link calendar-month-event-link--btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick(event);
                          }}
                        >
                          {content}
                        </button>
                      ) : event.external_url ? (
                        <a
                          href={event.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="calendar-month-event-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {content}
                        </a>
                      ) : (
                        <span className="calendar-month-event-link">{content}</span>
                      )}
                    </div>
                  );
                })}
                {dayEvents.length > 4 && (
                  <span className="calendar-month-event-more">+{dayEvents.length - 4} more</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
